import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, MapPin, Clock, Users, Plus, Heart, Search, Filter, ChevronDown } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { CreateSOSModal } from '@/components/SOS/CreateSOSModal';
import { SOSList } from '@/components/SOS/SOSList';
import { SOSMap } from '@/components/SOS/SOSMap';
import { Input } from '@/components/ui/input';
import { SOSDetailModal } from '@/components/SOS/SOSDetailModal';
import { EditSOSModal } from '@/components/SOS/EditSOSModal';
import { useLocation } from 'react-router-dom';
import sosHeroBg from '@/assets/sos-hero-bg.jpg';

interface SOSReport {
  id: string;
  user_id: string;
  type: 'lost' | 'found' | 'emergency' | 'danger';
  title: string;
  description: string | null;
  animal_species: string | null;
  animal_breed: string | null;
  animal_name: string | null;
  image_url: string | null;
  latitude: number;
  longitude: number;
  location_precision: 'precise' | 'street' | 'approximate';
  status: 'open' | 'spotted' | 'in_progress' | 'resolved' | 'cancelled';
  contact_phone: string | null;
  allow_public_sharing: boolean;
  last_spotted_at: string | null;
  helper_count: number;
  created_at: string;
  updated_at: string;
  profiles?: {
    username: string;
    display_name: string | null;
    avatar_url: string | null;
  };
}

const SOS = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [sosReports, setSosReports] = useState<SOSReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeTab, setActiveTab] = useState('list');
  const [filter, setFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [distanceFilter, setDistanceFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [selectedSOS, setSelectedSOS] = useState<SOSReport | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [editingSOS, setEditingSOS] = useState<SOSReport | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [totalHelpersCount, setTotalHelpersCount] = useState(0);
  const [openSOSCount, setOpenSOSCount] = useState(0);
  const [resolvedSOSCount, setResolvedSOSCount] = useState(0);

  useEffect(() => {
    // Set page title and SEO
    document.title = 'SOS - AstroPet';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Sistema di emergenza AstroPet - Segnala animali smarriti, trovati o in pericolo. Ricevi aiuto dalla community locale.');
    }

    // Add canonical URL
    const canonical = document.querySelector('link[rel="canonical"]') || document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    canonical.setAttribute('href', window.location.origin + '/app/sos');
    if (!document.querySelector('link[rel="canonical"]')) {
      document.head.appendChild(canonical);
    }
  }, []);

  useEffect(() => {
    fetchSOSReports();
    getUserLocation();
    
    // Set up real-time subscription for SOS reports updates
    const channel = supabase
      .channel('sos-reports-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'sos_reports'
        },
        (payload) => {
          console.log('SOS report updated:', payload);
          // Immediate counter updates for status changes
          if (payload.eventType === 'UPDATE') {
            const newStatus = payload.new?.status;
            const oldStatus = payload.old?.status;
            
            // Update counters immediately based on status change
            if (oldStatus !== newStatus) {
              if (newStatus === 'open' && oldStatus !== 'open') {
                setOpenSOSCount(prev => prev + 1);
              } else if (oldStatus === 'open' && newStatus !== 'open') {
                setOpenSOSCount(prev => Math.max(0, prev - 1));
              }
              
              if (newStatus === 'resolved' && oldStatus !== 'resolved') {
                setResolvedSOSCount(prev => prev + 1);
              } else if (oldStatus === 'resolved' && newStatus !== 'resolved') {
                setResolvedSOSCount(prev => Math.max(0, prev - 1));
              }
            }
          } else if (payload.eventType === 'INSERT') {
            const newStatus = payload.new?.status;
            if (newStatus === 'open') {
              setOpenSOSCount(prev => prev + 1);
            } else if (newStatus === 'resolved') {
              setResolvedSOSCount(prev => prev + 1);
            }
          } else if (payload.eventType === 'DELETE') {
            const oldStatus = payload.old?.status;
            if (oldStatus === 'open') {
              setOpenSOSCount(prev => Math.max(0, prev - 1));
            } else if (oldStatus === 'resolved') {
              setResolvedSOSCount(prev => Math.max(0, prev - 1));
            }
          }
          
          // Refresh all data for consistency
          fetchSOSReports();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'sos_helpers'
        },
        (payload) => {
          console.log('Helper status changed:', payload);
          
          // Immediate helper count updates
          if (payload.eventType === 'INSERT') {
            setTotalHelpersCount(prev => prev + 1);
          } else if (payload.eventType === 'DELETE') {
            setTotalHelpersCount(prev => Math.max(0, prev - 1));
          }
          
          // Refresh data for accuracy after a short delay
          setTimeout(() => {
            fetchSOSReports();
          }, 500);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Reload SOS reports when filter changes
  useEffect(() => {
    fetchSOSReports();
  }, [filter, statusFilter, user]);

  // Open detail modal from direct link (?sosId=...)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sosId = params.get('sosId');
    if (!sosId || sosReports.length === 0) return;

    const report = sosReports.find(r => r.id === sosId);
    if (report) {
      setActiveTab('list');
      setSelectedSOS(report);
      setShowDetailModal(true);
    }
  }, [location.search, sosReports]);

  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.error('Error getting location:', error);
        }
      );
    }
  };

  const fetchSOSReports = async () => {
    try {
      setLoading(true);
      
      // Always fetch ALL reports for accurate counters, then apply filters for display
      const { data: allReports, error: allReportsError } = await supabase
        .from('sos_reports')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (allReportsError) throw allReportsError;
      
      // Calculate global counters from ALL reports (not filtered)
      const allOpenReports = (allReports || []).filter(report => report.status === 'open');
      const allResolvedReports = (allReports || []).filter(report => report.status === 'resolved');
      
      // Update global counters
      setOpenSOSCount(allOpenReports.length);
      setResolvedSOSCount(allResolvedReports.length);
      
      // Count helpers only for OPEN emergencies across ALL reports
      const totalHelpers = allOpenReports.reduce((sum, report) => sum + (Number(report.helper_count) || 0), 0);
      setTotalHelpersCount(totalHelpers);
      
      // Now apply filters for the displayed reports
      let filteredReports = allReports || [];
      
      // Apply type filters
      if (filter === 'lost') {
        filteredReports = filteredReports.filter(r => r.type === 'lost');
      } else if (filter === 'found') {
        filteredReports = filteredReports.filter(r => r.type === 'found');
      } else if (filter === 'emergency') {
        filteredReports = filteredReports.filter(r => ['emergency', 'danger'].includes(r.type));
      } else if (filter === 'mine' && user) {
        filteredReports = filteredReports.filter(r => r.user_id === user.id);
      }

      // Apply status filters
      if (statusFilter === 'open') {
        filteredReports = filteredReports.filter(r => r.status === 'open');
      } else if (statusFilter === 'resolved') {
        filteredReports = filteredReports.filter(r => r.status === 'resolved');
      } else if (statusFilter === 'in_progress') {
        filteredReports = filteredReports.filter(r => r.status === 'in_progress');
      } else if (statusFilter === 'spotted') {
        filteredReports = filteredReports.filter(r => r.status === 'spotted');
      }
      
      // Fetch profiles for filtered reports
      const reportsWithProfiles = await Promise.all(
        filteredReports.map(async (report) => {
          const { data: profile } = await supabase
            .from('profiles')
            .select('username, display_name, avatar_url')
            .eq('user_id', report.user_id)
            .maybeSingle();
          
          return {
            ...report,
            profiles: profile || undefined,
            helper_count: Number(report.helper_count) || 0,
          };
        })
      );
      
      setSosReports(reportsWithProfiles as SOSReport[]);
      
      console.log('Global counters updated:', { 
        openSOS: allOpenReports.length, 
        totalHelpers, 
        resolved: allResolvedReports.length,
        displayedReports: reportsWithProfiles.length
      });
    } catch (error) {
      console.error('Error fetching SOS reports:', error);
      toast({
        title: "Errore",
        description: "Impossibile caricare i report SOS",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const R = 6371; // Radius of the Earth in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c; // Distance in kilometers
  };

  const filteredReports = sosReports.filter(report => {
    // Search filter
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = (
        report.title.toLowerCase().includes(searchLower) ||
        report.description?.toLowerCase().includes(searchLower) ||
        report.animal_species?.toLowerCase().includes(searchLower) ||
        report.animal_name?.toLowerCase().includes(searchLower) ||
        report.profiles?.display_name?.toLowerCase().includes(searchLower) ||
        report.profiles?.username?.toLowerCase().includes(searchLower)
      );
      if (!matchesSearch) return false;
    }

    // Distance filter
    if (distanceFilter !== 'all' && userLocation) {
      const distance = calculateDistance(
        userLocation.lat, 
        userLocation.lng, 
        report.latitude, 
        report.longitude
      );
      
      const maxDistance = parseInt(distanceFilter);
      if (distance > maxDistance) return false;
    }

    return true;
  }).sort((a, b) => {
    // Sort by distance if user location is available
    if (userLocation && distanceFilter !== 'all') {
      const distanceA = calculateDistance(userLocation.lat, userLocation.lng, a.latitude, a.longitude);
      const distanceB = calculateDistance(userLocation.lat, userLocation.lng, b.latitude, b.longitude);
      return distanceA - distanceB;
    }
    
    // Otherwise sort by creation date (newest first)
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  // Use state-based counters instead of calculating from sosReports

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'lost': return '🐕';
      case 'found': return '🔍';
      case 'emergency': return '🚨';
      case 'danger': return '⚠️';
      default: return '📍';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'lost': return 'Smarrito';
      case 'found': return 'Trovato';
      case 'emergency': return 'Emergenza';
      case 'danger': return 'Pericolo';
      default: return 'Generale';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'destructive';
      case 'spotted': return 'secondary';
      case 'in_progress': return 'default';
      case 'resolved': return 'outline';
      default: return 'secondary';
    }
  };

  const handleEditSOS = (sos: SOSReport) => {
    setEditingSOS(sos);
    setShowEditModal(true);
    setShowDetailModal(false);
  };

  return (
    <div 
      className="cosmic-background min-h-screen"
      style={{ backgroundImage: `url(${sosHeroBg})` }}
    >
      {/* Header */}
      <header className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-6 h-6 text-destructive" />
                <h1 className="text-2xl font-bold">SOS</h1>
              </div>
              {openSOSCount > 0 && (
                <Badge variant="destructive" className="flex items-center gap-1">
                  <Heart className="w-3 h-3" />
                  {openSOSCount} aperti nella tua zona
                </Badge>
              )}
            </div>
            
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground font-semibold"
              size="lg"
            >
              <Plus className="w-4 h-4 mr-2" />
              Invia SOS
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 space-y-6">
        {/* Search and Filters */}
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              placeholder="Cerca per luogo, animale, descrizione..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          
          {/* Type Filters */}
          <div className="flex flex-wrap gap-2">
            <Button
              variant={filter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('all')}
            >
              Tutti
            </Button>
            <Button
              variant={filter === 'lost' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('lost')}
            >
              🐕 Smarriti
            </Button>
            <Button
              variant={filter === 'found' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('found')}
            >
              🔍 Trovati
            </Button>
            <Button
              variant={filter === 'emergency' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('emergency')}
            >
              🚨 Emergenze
            </Button>
            {user && (
              <Button
                variant={filter === 'mine' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('mine')}
              >
                I miei SOS
              </Button>
            )}
          </div>

          {/* Advanced Filters Toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className="self-start"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filtri avanzati
            <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${showAdvancedFilters ? 'rotate-180' : ''}`} />
          </Button>

          {/* Advanced Filters */}
          {showAdvancedFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-card/50 rounded-lg border">
              <div className="space-y-2">
                <label className="text-sm font-medium">Stato</label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona stato" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti gli stati</SelectItem>
                    <SelectItem value="open">🔴 Aperti</SelectItem>
                    <SelectItem value="spotted">👀 Avvistati</SelectItem>
                    <SelectItem value="in_progress">⏳ In corso</SelectItem>
                    <SelectItem value="resolved">✅ Risolti</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Distanza</label>
                <Select value={distanceFilter} onValueChange={setDistanceFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona distanza" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte le distanze</SelectItem>
                    <SelectItem value="1">📍 1 km</SelectItem>
                    <SelectItem value="5">🚶 5 km</SelectItem>
                    <SelectItem value="10">🚗 10 km</SelectItem>
                    <SelectItem value="25">🛣️ 25 km</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Azioni</label>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setFilter('all');
                      setStatusFilter('all');
                      setDistanceFilter('all');
                      setSearchQuery('');
                    }}
                  >
                    Cancella filtri
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchSOSReports}
                  >
                    Aggiorna
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="glass border-white/20">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-destructive">{openSOSCount}</div>
              <div className="text-sm text-muted-foreground">SOS Aperti</div>
            </CardContent>
          </Card>
          <Card className="glass border-white/20">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-primary">
                {totalHelpersCount}
              </div>
              <div className="text-sm text-muted-foreground">Persone che aiutano</div>
            </CardContent>
          </Card>
          <Card className="glass border-white/20">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-green-500">
                {resolvedSOSCount}
              </div>
              <div className="text-sm text-muted-foreground">Casi risolti</div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="list" className="flex items-center gap-2">
              <span>📋</span> Lista
            </TabsTrigger>
            <TabsTrigger value="map" className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Mappa
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="list" className="mt-6">
            <SOSList 
              reports={filteredReports} 
              loading={loading}
              onRefresh={fetchSOSReports}
              userLocation={userLocation}
              onView={(report) => { setSelectedSOS(report); setShowDetailModal(true); }}
            />
          </TabsContent>
          
            <TabsContent value="map" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 relative">
              {/* Mappa */}
              <div className={`${selectedSOS && activeTab === 'map' ? 'lg:col-span-2' : 'col-span-full'} relative z-0`}>
                <SOSMap
                  reports={filteredReports}
                  userLocation={userLocation}
                  onReportClick={(report) => {
                    setSelectedSOS(report);
                    // In modalità mappa non apriamo il modal, mostriamo il pannello laterale
                    if (activeTab === 'map') {
                      // Il pannello si mostra automaticamente quando selectedSOS cambia
                    } else {
                      setShowDetailModal(true);
                    }
                  }}
                />
              </div>
              
              {/* Pannello dettagli laterale - visibile solo in modalità mappa */}
              {selectedSOS && activeTab === 'map' && (
                <div className="lg:col-span-1 relative z-10">
                  <Card className="h-fit sticky top-24 z-10">
                    <CardContent className="p-0">
                      <div className="p-4 border-b flex items-center justify-between">
                        <h3 className="font-semibold">Dettagli SOS</h3>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedSOS(null)}
                        >
                          ✕
                        </Button>
                      </div>
                      
                      <div className="p-4 space-y-4">
                        {/* Contenuto del SOS selezionato */}
                        <div className="flex items-center gap-3">
                          <div className="text-2xl">{getTypeIcon(selectedSOS.type)}</div>
                          <div>
                            <h4 className="font-medium">{selectedSOS.title}</h4>
                            <Badge variant={getStatusColor(selectedSOS.status) as any} className="text-xs">
                              {selectedSOS.status}
                            </Badge>
                          </div>
                        </div>
                        
                        {selectedSOS.animal_species && (
                          <div className="text-sm">
                            <span className="text-muted-foreground">Animale:</span> {selectedSOS.animal_species}
                            {selectedSOS.animal_breed && ` (${selectedSOS.animal_breed})`}
                            {selectedSOS.animal_name && ` - ${selectedSOS.animal_name}`}
                          </div>
                        )}
                        
                        {selectedSOS.description && (
                          <div className="text-sm">
                            <span className="text-muted-foreground">Descrizione:</span>
                            <p className="mt-1">{selectedSOS.description}</p>
                          </div>
                        )}
                        
                        {selectedSOS.image_url && (
                          <div>
                            <img 
                              src={selectedSOS.image_url} 
                              alt="Foto SOS"
                              className="w-full h-32 object-cover rounded"
                            />
                          </div>
                        )}
                        
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {selectedSOS.helper_count || 0} aiutano
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {new Date(selectedSOS.created_at).toLocaleDateString('it-IT')}
                          </div>
                        </div>
                        
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => {
                              setShowDetailModal(true);
                            }}
                            className="flex-1"
                          >
                            Vedi dettagli completi
                          </Button>
                          {user?.id === selectedSOS.user_id && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditSOS(selectedSOS)}
                            >
                              Modifica
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Emergency Banner */}
        <Card className="border-orange-200 bg-orange-50 dark:bg-orange-950/20 dark:border-orange-800">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5" />
              <div className="space-y-2">
                <h3 className="font-semibold text-orange-800 dark:text-orange-200">
                  Consigli per le emergenze
                </h3>
                <div className="text-sm text-orange-700 dark:text-orange-300 space-y-1">
                  <p>• Non inseguire un animale spaventato, mantieni la distanza</p>
                  <p>• Controlla sempre il microchip e il collare</p>
                  <p>• In caso di emergenza chiama: 112 (emergenze) o il veterinario più vicino</p>
                  <p>• Documenta tutto con foto e descrizioni dettagliate</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Floating SOS Button for mobile */}
      <div className="fixed bottom-6 right-6 md:hidden z-50">
        <Button
          onClick={() => setShowCreateModal(true)}
          size="lg"
          className="w-14 h-14 rounded-full bg-destructive hover:bg-destructive/90 text-destructive-foreground shadow-lg"
        >
          <Plus className="w-6 h-6" />
        </Button>
      </div>

      {/* Create SOS Modal */}
      <CreateSOSModal 
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        onSuccess={() => {
          fetchSOSReports();
          setShowCreateModal(false);
        }}
        userLocation={userLocation}
      />

      {/* SOS Detail Modal */}
      <SOSDetailModal 
        sos={selectedSOS}
        open={showDetailModal}
        onOpenChange={setShowDetailModal}
        onUpdate={() => {
          fetchSOSReports();
          setShowDetailModal(false);
        }}
        onEdit={handleEditSOS}
      />

      {/* Edit SOS Modal */}
      <EditSOSModal
        sos={editingSOS}
        open={showEditModal}
        onOpenChange={setShowEditModal}
        onSuccess={() => {
          fetchSOSReports();
          setShowEditModal(false);
          setEditingSOS(null);
        }}
      />
    </div>
  );
};

export default SOS;